import axios, { AxiosResponse } from "axios";
import { MerchantInterface, ResponseInterface } from "../interfaces";
import { Nullable } from "../interfaces/GenericTypes";
import { merchantFormat } from "../utils";
import { alertService } from "./alertService";

const testURL = "http://localhost:8085/vpos/ping";
const baseURL = "http://localhost:8085/vpos/metodo";
const printerURL = process.env.REACT_APP_API_PRINTER_HOST;
const defaultPrinter = "xp-80"; 

// Merchant misc options
enum MiscelaneousMerchantMethods {
  "imprimeUltimoVoucher",
  "imprimeUltimoVoucherP",
  "precierre",
  "cierre",
  "ultimoCierre",
}

// Vouchers available through the local printer service (GET /voucher/{command})
export type MerchantVoucherCommand =
  | "aprobado"
  | "procesado"
  | "precierre"
  | "cierre";

// Request header
const header = {
  headers: {
    "Content-Type": "application/json",
  },
};

// Ping merchant service
export const pingMerchant = async (): Promise<Nullable<MerchantInterface>> => {
  try {
    const response = await axios.get(testURL);

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return null;
};

// Print the voucher generated by a merchant operation through the local
// printer service, without holding up the flow that triggered it
const printMerchantResponseVoucher = (
  merchant: Nullable<MerchantInterface>
) => {
  if (!merchant?.nombreVoucher) return;

  printMerchantVoucherFile(merchant.nombreVoucher).then((printed) => {
    if (printed.didError) {
      alertService.error(
        "No se pudo imprimir el voucher",
        printed.errorMessage
      );
    }
  });
};

// Pay through merchant service
export const payMerchant = async (
  amount: number,
  ci: string,
  zelleDescription?: string
): Promise<Nullable<MerchantInterface>> => {
  const amountString = merchantFormat(amount);

  const body = {
    accion: "tarjeta",
    montoTransaccion: amountString,
    cedula: ci,
    referencia: zelleDescription,
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      const merchant: Nullable<MerchantInterface> = response.data;

      printMerchantResponseVoucher(merchant);

      return merchant;
    }
  } catch (error) {}

  return null;
};

export const annulationMerchant = async (
  seq: string
): Promise<Nullable<MerchantInterface>> => {
  const body = {
    accion: "anulacion",
    numSeq: seq,
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      const merchant: Nullable<MerchantInterface> = response.data;

      printMerchantResponseVoucher(merchant);

      return merchant;
    }
  } catch (error) {}

  return null;
};

// Pay change with merchant service
export const changeMerchant = async (
  amount: number,
  ci: string
): Promise<Nullable<MerchantInterface>> => {
  const amountString = merchantFormat(amount);
  const body = {
    accion: "cambio",
    montoTransaccion: amountString,
    cedula: ci,
    tipoMoneda: "VES",
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      const merchant: Nullable<MerchantInterface> = response.data;

      printMerchantResponseVoucher(merchant);

      return merchant;
    }
  } catch (error) {}

  return null;
};

// Use other merchant services
export const miscMerchant = async (
  command: number
): Promise<Nullable<MerchantInterface>> => {
  const body = {
    accion: MiscelaneousMerchantMethods[command],
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      const merchant: Nullable<MerchantInterface> = response.data;

      // The precierre voucher only prints when the vpos approves the
      // operation (codRespuesta "00"). The cierre voucher never prints
      // here: it is shown in a modal where the user can print it
      const skipPrint =
        command === MiscelaneousMerchantMethods.precierre &&
        merchant?.codRespuesta !== "00";

      if (!skipPrint && command !== MiscelaneousMerchantMethods.cierre) {
        printMerchantResponseVoucher(merchant);
      }

      return merchant;
    }
  } catch (error) {}

  return null;
};

// Perform a request to the local printer service, normalizing failures to
// the ResponseInterface shape
const requestVoucherService = async (
  request: () => Promise<AxiosResponse<ResponseInterface<string>>>,
  failMessage: string
): Promise<ResponseInterface<string>> => {
  try {
    const response = await request();

    if (response.status === 200) {
      return response.data;
    }

    return {
      message: "",
      didError: true,
      errorMessage: failMessage,
      model: null,
    };
  } catch (error) {
    // The service answers with BadRequest and the same shape when it fails
    const data = (error as any)?.response?.data as
      | ResponseInterface<string>
      | undefined;

    return {
      message: data?.message ?? "",
      didError: true,
      errorMessage:
        data?.errorMessage ??
        "No se pudo comunicar con el servicio de impresión, verifique que el programa se esté ejecutando.",
      model: null,
    };
  }
};

// Get the text of a merchant voucher from the local printer service
export const getMerchantVoucher = async (
  command: MerchantVoucherCommand
): Promise<ResponseInterface<string>> =>
  await requestVoucherService(
    () =>
      axios.post<ResponseInterface<string>>(
        `${printerURL}/voucher/${command}`,
        header
      ),
    `No se pudo obtener el voucher '${command}'.`
  );

// Get the text of a voucher file given its full path on the machine that
// runs the local printer service
export const getMerchantVoucherFile = async (
  filePath: string
): Promise<ResponseInterface<string>> =>
  await requestVoucherService(
    () =>
      axios.post<ResponseInterface<string>>(
        `${printerURL}/voucher/${encodeURIComponent(filePath)}`,
        header
      ),
    "No se pudo obtener el voucher del cierre."
  );

// Get the voucher text and notify the user when it could not be read
export const getMerchantVoucherText = async (
  command: MerchantVoucherCommand
): Promise<Nullable<string>> => {
  const response = await getMerchantVoucher(command);

  if (response.didError || !response.model) {
    alertService.error("No se pudo obtener el voucher", response.errorMessage);
    return null;
  }

  return response.model;
};

// Send a voucher to print through the local printer service
// (POST /voucher/{command}/print?printer={printer}). The command accepts the
// same values as the voucher visualization, or the full path of a voucher file
const requestMerchantVoucherPrint = async (
  command: string,
  printer: string = defaultPrinter
): Promise<ResponseInterface<string>> =>
  await requestVoucherService(
    () =>
      axios.post<ResponseInterface<string>>(
        `${printerURL}/voucher/${encodeURIComponent(command)}/print`,
        null,
        { ...header, params: { printer } }
      ),
    `No se pudo imprimir el voucher '${command}'.`
  );

// Print one of the vouchers available through the local printer service,
// using the same commands as the voucher visualization
export const printMerchantVoucher = async (
  command: MerchantVoucherCommand,
  printer?: string
): Promise<ResponseInterface<string>> =>
  await requestMerchantVoucherPrint(command, printer);

// Print a voucher file given its full path on the machine that runs the
// local printer service
export const printMerchantVoucherFile = async (
  filePath: string,
  printer?: string
): Promise<ResponseInterface<string>> =>
  await requestMerchantVoucherPrint(filePath, printer);

// Voucher of the last approved transaction (vpos.txt)
export const getApprovedVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("aprobado");

// Voucher of the last processed transaction (ultTxnProc.txt)
export const getProcessedVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("procesado");

// Latest pre-closing report (precierres folder)
export const getPreClosingVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("precierre");

// Latest closing report (cierres folder)
export const getClosingVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("cierre");
